/* =========================================================================
   "Light a Candle" overlay — the reverent memorial flow behind the home candle
   chip. Flow: name prompt → the candle lights → the Eternal Rest prayer →
   a short factoid → (optional) the Remembrances list. A gentle modal over
   Home; NOT a game screen. Deliberately free of scoring/celebration mechanics.

   Storage + prayer text live in memorial.ts (pure, unit-tested); this file is
   the DOM/interaction seam only.
   ========================================================================= */
import { esc } from './engine';
import { byId } from './dom';
import { track } from './telemetry';
import {
  lightCandleFor,
  eternalRest,
  factoidFor,
  getRemembrances,
  normalizeName,
  type Prayer,
} from './memorial';

export const candlePrayerHTML = /* html */ `
<div id="candleOverlay" class="candle-ov hidden" aria-hidden="true">
  <div class="candle-dim" id="candleDim"></div>
  <div class="candle-card" role="dialog" aria-modal="true" aria-label="Light a candle">
    <button class="candle-close" id="candleClose" type="button" aria-label="Close">✕</button>
    <div class="candle-flame" id="candleFlame" aria-hidden="true">
      <span class="cf-glow"></span><span class="cf-fire">🕯️</span>
    </div>
    <div class="candle-body" id="candleBody"></div>
  </div>
</div>`;

let onClose: (() => void) | null = null;

/** Open the candle overlay. `after` runs when it closes (to repaint home chip). */
export function openCandlePrayer(after?: () => void): void {
  onClose = after || null;
  track('AreaOpened', { area: 'candle' });
  const ov = byId('candleOverlay');
  ov.classList.remove('hidden');
  ov.setAttribute('aria-hidden', 'false');
  byId('candleFlame').classList.remove('is-lit');
  renderPrompt();
}

function close(): void {
  const ov = byId('candleOverlay');
  ov.classList.add('hidden');
  ov.setAttribute('aria-hidden', 'true');
  const cb = onClose;
  onClose = null;
  if (cb) cb();
}

/* -------------------------------- steps -------------------------------- */

function renderPrompt(): void {
  byId('candleBody').innerHTML =
    `<div class="candle-kicker">🕯️ Light a candle</div>` +
    `<div class="candle-lead">Remember someone you’ve lost. Enter their name to light a candle and pray for them.</div>` +
    `<input class="candle-input" id="candleName" type="text" maxlength="60" ` +
    `placeholder="A loved one’s name (optional)" autocomplete="off" />` +
    `<button class="btn candle-cta" id="candleLightBtn" type="button">Light the candle</button>` +
    remembrancesHTML();
  const input = byId<HTMLInputElement>('candleName');
  byId('candleLightBtn').addEventListener('click', () => light(input.value));
  input.addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Enter') light(input.value);
  });
  input.focus();
}

function light(rawName: string): void {
  const name = normalizeName(rawName);
  lightCandleFor(name);
  track('CandleLit', { named: name ? 1 : 0 });
  byId('candleFlame').classList.add('is-lit');
  renderPrayer(eternalRest(name), getRemembrances().length);
}

function renderPrayer(prayer: Prayer, remembranceCount: number): void {
  const factoid = factoidFor(remembranceCount);
  byId('candleBody').innerHTML =
    `<div class="candle-kicker">${esc(prayer.title)}</div>` +
    `<div class="candle-prayer">${prayer.lines.map((l) => esc(l)).join('<br/>')}</div>` +
    `<div class="candle-factoid">${esc(factoid)}</div>` +
    `<button class="btn secondary candle-cta" id="candleDoneBtn" type="button">Amen</button>` +
    remembrancesHTML();
  byId('candleDoneBtn').addEventListener('click', close);
}

function remembrancesHTML(): string {
  const list = getRemembrances();
  if (!list.length) return '';
  const rows = list
    .slice(0, 12)
    .map(
      (r) =>
        `<div class="candle-remembrance"><span class="cr-name">${esc(r.name)}</span>` +
        (r.times > 1 ? `<span class="cr-times">${r.times}×</span>` : '') +
        `</div>`,
    )
    .join('');
  return (
    `<div class="candle-remembrances">` +
    `<div class="candle-remembrances-title">In your prayers</div>` +
    rows +
    `</div>`
  );
}

/** Wire the overlay's chrome once at boot (dim + close dismiss). */
export function initCandlePrayer(): void {
  byId('candleClose').addEventListener('click', close);
  byId('candleDim').addEventListener('click', close);
}
